import React, { useContext, useEffect, useRef, useState } from 'react';

//context
import { IInsightsContext, InsightsContext } from '@/context/InsightsContext';

//helpers
import { columnsContentPop, columnsChanPop, columnsListPop, columnsUserGroup } from '@/data/insightsColumns';
import { breakDownConvert, optionsClean } from  '@/helpers/Insights';
import { removeTimeZone, today } from  '@/helpers/Dates';
import { BuildPath } from  '@/helpers/Paths';
import {tooltipData} from '../../../data/insights';

//interfaces
import { IApiSettings, IHttpRequest } from  '@/interfaces/IHttp';
import { Direction, IBreakdown, IDateRange, initBreakdown, initUserDevices, IOption, IUserDevices, OrderBy } from  '@/interfaces/insightsp2';
import { IContentTable, initContentTable } from '../../../interfaces/IContentTable';

//components
import { container, TYPES } from "@/inversifyconfig/container.config";
import { Tooltip as ReactTooltip } from 'react-tooltip';
import ContentTable from '../../elements/insights/ContentTable';
import ToolTipWrapper from '../Tooltip/ToolTipWrapper';
import Breakdown from './Breakdown';
import DateRange from './DateRange';
import UsersDevices from './UsersDevices';
import CustomSelect from './CustomSelect';
import GenError from './GenError';

//css
import  '@/css/breakdown.scss';


const Overview = () => {

    // Context 
    const context:IInsightsContext = useContext(InsightsContext);

    // Dropdowns
    const [userDevicesDates, setUserDevicesDates] = useState<IDateRange>(today());
    const [workspaces, setWorkspaces] = useState<IOption[]>(null);
    const [selectedWorkSpace, setSelectedWorkSpace] = useState(null);

    // Order by
    const [contentPopOrderBy, setContentPopOrderBy] = useState<OrderBy>({column:"views", direction:Direction.dsc});
    const [userGroupOrderBy, setUserGroupOrderBy] = useState<OrderBy>({column:"uniqueViews", direction:Direction.dsc});
    const [listPopOrderBy, setListPopOrderBy] = useState<OrderBy>({column:"views", direction:Direction.dsc});
    const [chanPopOrderBy, setChanPopOrderBy] = useState<OrderBy>({column:"views", direction:Direction.dsc});

    // Data
    const [userDevicesData, setUserDevicesData] = useState<IUserDevices>(initUserDevices);
    const [breakdown, setBreakdown] = useState<IBreakdown[]>(initBreakdown);
    const [contentPop, setContentPop] = useState<IContentTable>(initContentTable);
    const [userGroup, setUserGroup] = useState<IContentTable>(initContentTable);
    const [listPop, setListPop] = useState<IContentTable>(initContentTable);
    const [chanPop, setChanPop] = useState<IContentTable>(initContentTable);

    const [breakdownCsv, setBreakdownCsv] = useState<string>("");

    // Loading
    const [initLoad, setInitLoad] = useState<boolean>(true);
    const [genError, setGenError] = useState<boolean>(false);

    // API
    const apiSettings = container.get<IApiSettings>(TYPES.IApiSettings);

    // Ref
    const workspaceSelectRef = useRef(null);

    // Initial load
    useEffect(() => {

        // Loading with promises
        context.setLoading(true);
        context.loadTracker.setLoadMin(6);

        // Workspaces
        container.get<IHttpRequest>(TYPES.IHttpRequest).get(`insights/workspaces`).then((x) => {

            const options:IOption[] = [];

            if (x && x.data){
                x.data.spaces.forEach((y:any) => {
                    options.push({
                        value:y.guid,
                        label:y.title
                    });
                });
            }

            const cleaned = optionsClean(options);

            // Default all
            cleaned.unshift({
                value:null,
                label:'All Workspaces'
            });

            setWorkspaces(cleaned);
            
        }).catch((error) => {
            console.error(`Workspaces error ${error}`)
        })

        // Aggregated data for tables
        aggregatedData();

    }, []);

    // Gen error hide loading
    useEffect(() => {
        if (genError){
            context.setLoading(false);
        }
    }, [genError]);

    // Devices or Breakdown change
    useEffect(() => {
       
        if (!initLoad){

            context.setLoading(true);

            // Aggregated data for tables
            aggregatedData();          
        }    
    }, [selectedWorkSpace, userDevicesDates]);

    // Content pop change
    useEffect(() => {

        if (!initLoad){

            context.setLoading(true);

            container.get<IHttpRequest>(TYPES.IHttpRequest).get(`insights/content-popularity?${queryString()}&orderBy=${contentPopOrderBy.column}&direction=${contentPopOrderBy.direction}`).then((x) => {

                setContentPop({
                    title: contentPop.title,
                    tooltip:contentPop.tooltip,
                    data:x.data.data,
                    columns:columnsContentPop(),
                    csvDownload: `${apiSettings.apiBaseUrl()}insights/content-popularity-csv?${queryString()}`
                });
        
            }).catch((error) => {
                console.error(`Content pop change error ${error}`);
            }).finally(() => {
                context.setLoading(false);
            });

        }    

    }, [contentPopOrderBy]); 

    // User group activity
    useEffect(() => {

        if (!initLoad){

            context.setLoading(true);

            container.get<IHttpRequest>(TYPES.IHttpRequest).get(`insights/user-group-activity?${queryString()}&top=100&orderBy=${userGroupOrderBy.column}&direction=${userGroupOrderBy.direction}`).then((x) => {
                
                setUserGroup({
                    title:userGroup.title,
                    data: x.data.data,
                    columns:columnsUserGroup(),
                    csvDownload: `${apiSettings.apiBaseUrl()}insights/user-group-activity-csv?${queryString()}`
                });
        
            }).catch((error) => {
                console.error(`User group activity error ${error}`);
            }).finally(() => {
                context.setLoading(false);
            });

        }    

    }, [userGroupOrderBy]);

    // List popularity
    useEffect(() => {

        if (!initLoad){

            context.setLoading(true);

            container.get<IHttpRequest>(TYPES.IHttpRequest).get(`insights/list-popularity?${queryString()}&top=100&orderBy=${listPopOrderBy.column}&direction=${listPopOrderBy.direction}`).then((x) => {
                
                setListPop({
                    title:listPop.title,
                    data:x.data.data,
                    columns:columnsListPop(),
                    csvDownload: `${apiSettings.apiBaseUrl()}insights/list-popularity-csv?${queryString()}`
                });
        
            }).catch((error) => {
                console.error(`List popularity error ${error}`);
            }).finally(() => {
                context.setLoading(false);
            });

        }    

    }, [listPopOrderBy]);

    // Channel popularity
    useEffect(() => {

        if (!initLoad){

            context.setLoading(true);

            container.get<IHttpRequest>(TYPES.IHttpRequest).get(`insights/channel-popularity?${queryString()}&top=100&orderBy=${chanPopOrderBy.column}&direction=${chanPopOrderBy.direction}`).then((x) => {
                
                setChanPop({
                    title:chanPop.title,
                    data:x.data.data,
                    columns:columnsChanPop(),
                    csvDownload: `${apiSettings.apiBaseUrl()}insights/channel-popularity-csv?${queryString()}`
                });
        
            }).catch((error) => {
                console.error(`Channel popularity error ${error}`);
            }).finally(() => {
                context.setLoading(false);
            });

        }    

    }, [chanPopOrderBy]);

    function aggregatedData():void {

        // Reset loading count
        context.loadTracker.dispatch({type: 'reset'});

        container.get<IHttpRequest>(TYPES.IHttpRequest).get(`insights/aggregated?${queryString()}`).then((x) => {
            
            // User devices
            x.data.insightsUsersAndDevicesResource.csvDownload = `${apiSettings.apiBaseUrl()}insights/users-and-devices-csv?${queryString()}`
            setUserDevicesData(x.data.insightsUsersAndDevicesResource);

            // Breakdown
            setBreakdownCsv(`${apiSettings.apiBaseUrl()}insights/workspace-insights-csv?${queryString()}`);
            setBreakdown(breakDownConvert(x.data.insightsWorkspaceInsightsResource));

            // Content popularity
            setContentPop({
                title:'Content Popularity Overview',
                tooltip:tooltipData.contentPopOverview,
                data:x.data.insightsContentPopularityResource.data,
                columns:columnsContentPop(),
                csvDownload: `${apiSettings.apiBaseUrl()}insights/content-popularity-csv?${queryString()}`
            });

            // User groups
            setUserGroup({
                title:'User Group Activity Overview',
                tooltip: tooltipData.userGroup,
                data:x.data.insightsUserGroupActivityResource.data,
                columns:columnsUserGroup(),
                csvDownload: `${apiSettings.apiBaseUrl()}insights/user-group-activity-csv?${queryString()}`
            });

            // List pop
            setListPop({
                title:'List Popularity',
                tooltip: tooltipData.listPop,
                data:x.data.insightsListPopularityResource.data,
                columns:columnsListPop(),
                csvDownload: `${apiSettings.apiBaseUrl()}insights/list-popularity-csv?${queryString()}`
            });
            // Chan pop
            setChanPop({
                title:'Workspace Activity',
                tooltip: tooltipData.channelAct,
                data:x.data.insightsChannelPopularityResource.data,
                columns:columnsChanPop(),
                csvDownload: `${apiSettings.apiBaseUrl()}insights/channel-popularity-csv?${queryString()}`
            }); 

        }).catch((error) => {
            console.error(`Aggregated error ${error}`);
            setGenError(true)
        }).finally(() => {
            setInitLoad(false);
        });

    }

    function queryString():string {

        let dates;
        let workspace = "";

        // Dates
        if (userDevicesDates){
            dates = `dateFrom=${removeTimeZone(userDevicesDates.dateFrom)}&dateTo=${removeTimeZone(userDevicesDates.dateTo)}`;
        }
        else{
            dates = `dateFrom=${removeTimeZone(today().dateFrom)}&dateTo=${removeTimeZone(today().dateTo)}`;
        }

        // Workspaces
        if (selectedWorkSpace && selectedWorkSpace.value){
            workspace = `&workspaceGuids=${selectedWorkSpace.value}`
        }
 
        return `${dates}${workspace}`
    }

    // Show nothing until has data
    if (initLoad){
        return null;
    }

    // Gen error
    if (genError){
        return (
            <GenError />
        )
    }

    return (
        <>

            <div className="in-selector overview-opt-1">

                <span>Showing insights for</span>
                
                <DateRange 
                    setDateRange={setUserDevicesDates}  
                    placeholder="Today"
                />

                <ToolTipWrapper id="insights-UsersDevices" className="insights-UsersDevices" >                     
                    <ReactTooltip place="top" className='thrive-tooltip' delayHide={1000}  id="insights-UsersDevices" >
                        <span>Choose the time range you want to view statistics for</span>
                    </ReactTooltip>
                </ToolTipWrapper>

            </div>

            <UsersDevices data={userDevicesData} />

            <div className="breakdown-container">

                <div className="controls">

                    <span>Showing insights for</span>

                    <div className="react-selector">
                     
                        <CustomSelect 
                            options={workspaces} 
                            optionsChange={(spaces) => setSelectedWorkSpace(spaces[0])} 
                            ref={workspaceSelectRef} 
                            multi={false} 
                            showCalendar={false} 
                            placeholder="All Workspaces"
                            defaultSelectedIndex={0}
                        />

                    </div>

                    <ToolTipWrapper id="breakdown-tooltip" className="breakdown-tooltip" >                     
                        <ReactTooltip place="top" className='thrive-tooltip' delayHide={1000}  id="breakdown-tooltip" >
                            <span>Choose a workspace</span>
                        </ReactTooltip>
                    </ToolTipWrapper>

                </div>

                <Breakdown data={breakdown} queryString={queryString()} />

                <div className="download">
                    <a className="download-stats" href={breakdownCsv} aria-label='Download'>
                        <img src={`${BuildPath()}/images/insightsp2/DownloadStatsBtn.svg`} alt="" />
                    </a>
                </div>

            </div>
            
            <ContentTable content={contentPop} className="content-table-8" setOrder={setContentPopOrderBy} defaultOrder={contentPopOrderBy} />
          
            <ContentTable content={userGroup} className="content-table-9" setOrder={setUserGroupOrderBy} defaultOrder={userGroupOrderBy} />

            <div className="two-col">

                <ContentTable content={listPop} className="content-table-5" setOrder={setListPopOrderBy} defaultOrder={listPopOrderBy} />

                <ContentTable content={chanPop} className="content-table-5" setOrder={setChanPopOrderBy} defaultOrder={chanPopOrderBy} />

            </div>
            
        </>
    );
}

export default Overview;